import * as _ from 'lodash';
import BWDataService from './bw-data.service';
import { AccountModel } from 'src/types/account.model';
import { BudgetModel } from 'src/types/budget.model';
import { LocalDate } from '@js-joda/core';
import FormatService from './format.service';
import { CategoryModel } from 'src/types/category.model';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import { BudgetSummaryModel } from 'src/types/budget-summary.model';
import {
    IconActivity,
    IconAirplane,
    IconBank,
    IconBusiness,
    IconDining,
    IconGuide,
    IconEntertainment,
    IconGrid,
    IconHome,
    IconInfinity,
    IconMedical,
    IconMerchandise,
    IconPartnership
} from '@americanexpress/dls-react';
import React from 'react';
import CategoryService from 'src/services/category.service';

export const BUDGET_OVERALL_ID = '00000000-0000-0000-0000-000000000000';

export class BudgetUpdate {
    groupId?: string;
    monthlyAmount: number;
    enabled?: boolean = true;
}

export class Summary {
    accountErrors: AccountModel[];
    overBudget: number;
    warnBudget: number;
    countBudgets: number;
}

export class BudgetHistoryItem {
    startDate: LocalDate;
    endDate: LocalDate;
    total: number;
}

class BudgetService extends BWDataService {
    public static async getBudget(): Promise<BudgetModel[]> {
        return await this.get('/budgets');
    }

    public static async updateBudget(budget: BudgetModel): Promise<BudgetModel> {
        const payload: Partial<BudgetModel> = {
            groupId: budget.groupId,
            monthlyAmount: budget.monthlyAmount
        };

        return await this.put(`/budgets/${budget.groupId}`, payload);
    }

    public static async updateBudgets(budgets: BudgetUpdate[]): Promise<void> {
        const payload: Array<Partial<BudgetModel>> = budgets.map((budget) => {
            return {
                groupId: budget.groupId,
                monthlyAmount: budget.monthlyAmount
            };
        });

        return await this.put('/budgets', payload);
    }

    public static async deleteBudget(budget: BudgetModel): Promise<void> {
        await this.delete(`/budgets/${budget.groupId || ''}`);
    }

    public static async getABudget(groupId: string): Promise<BudgetModel> {
        return await this.get('/budgets/' + groupId);
    }

    public static async getSummary(): Promise<Summary> {
        return await this.get('/summary');
    }

    public static async getSuggested(): Promise<BudgetModel[]> {
        return await this.get('/budgets/suggested');
    }

    public static getMonthlyBudget(budgets: BudgetModel[]): BudgetModel {
        return budgets?.find((budget) => budget.groupId === BUDGET_OVERALL_ID);
    }

    // We want to first search for budget with given groupId, if that doesn't exist then use monthly budget if possible,
    // else resort to the first budget in array.
    public static getBudgetOrMonthlyOrFirst(budgets: BudgetModel[], groupId: string): BudgetModel {
        let budget: BudgetModel;
        if (groupId) {
            budget = budgets?.find((budget) => budget.groupId === groupId);
        }
        if (!budget) {
            budget = this.getMonthlyBudget(budgets);
        }
        return budget || budgets?.[0];
    }

    public static getBudgetColor(actualAmount: number, budgetAmount: number): string {
        return actualAmount <= budgetAmount ? 'under' : 'over';
    }

    public static async getBudgetSummary(): Promise<BudgetSummaryModel> {
        return await this.get('/budgets/summary');
    }

    public static getProgressPercent(actualAmount: number, budgetAmount: number): number {
        return budgetAmount ? Math.min((Math.max(0, actualAmount) / budgetAmount) * 100, 100) : 0;
    }

    public static budgetsGroupedByCategory(budgets: BudgetModel[], categories: CategoryModel[]): BudgetModel[][] {
        return CategoryService.groupByCategory(budgets, categories, 'groupName') as BudgetModel[][];
    }

    /** returns the monthly budget amount if there is a monthly budget set,
     * otherwise returns the sum of the monthly amounts of all parent category budgets */
    public static monthlyBudgetTotal(budgets: BudgetModel[], categories: CategoryModel[]): number {
        const monthlyBudget = budgets?.find((budget) => budget.groupId === BUDGET_OVERALL_ID);
        if (monthlyBudget) return monthlyBudget.monthlyAmount;
        return _.sumBy(BudgetService.parentBudgets(budgets, categories), 'monthlyAmount');
    }

    /** returns the sum of the currently spent amounts of all parent category budgets */
    public static currentlySpentCategoryTotal(budgets: BudgetModel[], categories: CategoryModel[]): number {
        return _.sumBy(BudgetService.parentBudgets(budgets, categories), 'currentlySpent');
    }

    /** returns an array of all parent category budgets, excluding the overall monthly budget */
    public static parentBudgets(budgets: BudgetModel[], categories: CategoryModel[]): BudgetModel[] {
        return budgets?.filter((budget) => {
            const budgetCategory = categories?.find((category) => category.id === budget.groupId);
            const parentBudget = budgets?.find((bud) => bud.groupId === budgetCategory?.parentId);
            return !parentBudget && budget.groupId !== BUDGET_OVERALL_ID;
        });
    }

    /** checks if the given page is the Budget Settings page */
    public static isBudgetSettingsPage(page: string): boolean {
        return BudgetPages.SETTINGS === page;
    }

    /** returns the overall monthly budget */
    public static getOverallMonthlyBudget(budgets: BudgetModel[]): BudgetModel {
        return budgets?.filter((budget: BudgetModel) => budget.groupId === BUDGET_OVERALL_ID)?.[0];
    }

    /** returns category budgets, which excludes the overall monthly budget */
    public static getCategoryBudgets(budgets: BudgetModel[]): BudgetModel[] {
        return budgets?.filter((budget: BudgetModel) => budget.groupId !== BUDGET_OVERALL_ID);
    }

    /** return the monthly amount total of all the provided parent category budgets */
    public static getAllParentCategoriesTotal(budgets: BudgetModel[]): number {
        const allParentCategories = budgets?.filter(
            (budget: BudgetModel) => !budget.parentGroupId && budget.groupId !== BUDGET_OVERALL_ID
        );
        return _.sumBy(allParentCategories, (budget) => {
            return Number(budget.monthlyAmount || '0');
        });
    }

    /** return the monthly amount total of all subcategory budgets for a parent category budget */
    public static getParentCategoryTotal(budgets: BudgetModel[], parentGroupId: string): number {
        const subcategoryBudgets = budgets.filter((budget: BudgetModel) => budget.parentGroupId === parentGroupId);
        return _.sumBy(subcategoryBudgets, (budget) => {
            return Number(budget.monthlyAmount || '0');
        });
    }

    /** return the monthly amount of a given parent category budget */
    public static getParentBudgetMonthlyAmount(budgets: BudgetModel[], parentGroupId: string): number {
        return budgets.find((budget: BudgetModel) => budget.groupId === parentGroupId)?.monthlyAmount;
    }

    /** return the amount available for a parent category budget */
    public static getCategoryAmountAvailable(budgets: BudgetModel[], parentGroupId: string): string {
        const categoryTotal = this.getParentCategoryTotal(budgets, parentGroupId);
        const monthlyAmount = this.getParentBudgetMonthlyAmount(budgets, parentGroupId);
        if (!monthlyAmount || !categoryTotal) return '$--';
        return FormatService.formatCurrency(Math.max(monthlyAmount - categoryTotal, 0));
    }

    /** checks if there are any more subcategory or category budget options that can be added to the budget
     *  personalize */
    public static hasAvailableCategoryOptions(
        budgets: BudgetModel[],
        categories: CategoryModel[],
        addingSubcategory: boolean = false,
        index?: number
    ): boolean {
        const categoryOptions: CategoryModel[] = categories?.filter((category) => {
            return addingSubcategory ? category.parentId === budgets[index].groupId : !category.parentId;
        });

        const categoriesRemaining: BudgetModel[] = budgets?.filter((budget) => {
            return addingSubcategory
                ? budget.parentGroupId === budgets[index].groupId
                : !budget.parentGroupId && budget.groupId !== BUDGET_OVERALL_ID;
        });

        return categoriesRemaining.length < categoryOptions.length;
    }

    // TODO: (Optional Enhancement) Add unit test with react testing library for this service. Currently, we have not
    //  installed react testing library to avoid more dependency security issues. Once we have settled these issues,
    //  we should see if we can test with react testing library, as we need it to render a component during unit test.
    public static getBudgetIconComponent(key: string): JSX.Element {
        const icons: { [key: string]: JSX.Element } = {
            Education: <IconGuide title={key} />,
            'Student Loan': <IconGuide title={key} />,
            Tuition: <IconGuide title={key} />,

            Family: <IconPartnership title={key} />,
            'Extracurricular Activities': <IconPartnership title={key} />,
            'Family Care': <IconPartnership title={key} />,
            Pets: <IconPartnership title={key} />,

            Financial: <IconBank title={key} />,
            'Cash & ATM': <IconBank title={key} />,
            Checks: <IconBank title={key} />,
            'Donations & Gifts': <IconBank title={key} />,
            'Income & Deposits': <IconBank title={key} />,
            'Interest Charged': <IconBank title={key} />,
            Insurance: <IconBank title={key} />,
            Investments: <IconBank title={key} />,
            Loans: <IconBank title={key} />,
            'Payments & Transfers': <IconBank title={key} />,
            Reimbursement: <IconBank title={key} />,
            Savings: <IconBank title={key} />,
            Taxes: <IconBank title={key} />,

            'Food & Dining': <IconDining title={key} />,
            Groceries: <IconDining title={key} />,
            Restaurants: <IconDining title={key} />,
            'Takeout & Delivery': <IconDining title={key} />,

            'Fun & Entertainment': <IconEntertainment title={key} />,
            Attractions: <IconEntertainment title={key} />,
            'Movies, Live Music, & Theater': <IconEntertainment title={key} />,
            'Newspapers & Magazines': <IconEntertainment title={key} />,
            'Sporting Events & Recreation': <IconEntertainment title={key} />,
            'Streaming Services': <IconEntertainment title={key} />,

            'Health & Fitness': <IconMedical title={key} />,
            'Beauty & Personal Care': <IconMedical title={key} />,
            Fitness: <IconMedical title={key} />,
            'Healthcare & Pharmacy': <IconMedical title={key} />,

            Home: <IconHome title={key} />,
            'Appliances, Furniture, & Decor': <IconHome title={key} />,
            'Home & Rental Insurance': <IconHome title={key} />,
            'Phone, Internet, & Cable Service': <IconHome title={key} />,
            'Rent & Mortgage': <IconHome title={key} />,
            Utilities: <IconHome title={key} />,

            Services: <IconBusiness title={key} />,
            'Cleaning Services': <IconBusiness title={key} />,
            'Device Repair & Services': <IconBusiness title={key} />,
            'Government & Community Services': <IconBusiness title={key} />,
            'Laundry & Dry Cleaning': <IconBusiness title={key} />,
            Legal: <IconBusiness title={key} />,
            Photography: <IconBusiness title={key} />,
            'Printing & Shipping': <IconBusiness title={key} />,

            Shopping: <IconMerchandise title={key} />,
            'Books, Movies, & Music': <IconMerchandise title={key} />,
            'Clothing & Accessories': <IconMerchandise title={key} />,
            'Computers & Electronics': <IconMerchandise title={key} />,
            'Sporting Goods, Toys, & Hobbies': <IconMerchandise title={key} />,

            'Travel & Transportation': <IconAirplane title={key} />,
            'Air Travel': <IconAirplane title={key} />,
            'Automobile Payments & Insurance': <IconAirplane title={key} />,
            'Gas & Fuel': <IconAirplane title={key} />,
            'Hotels & Vacation Rentals': <IconAirplane title={key} />,
            Parking: <IconAirplane title={key} />,
            'Public Transportation': <IconAirplane title={key} />,
            'Rental Cars, Ride Shares, & Taxis': <IconAirplane title={key} />,

            Overall: <IconInfinity title={key} />,
            Uncategorized: <IconActivity title={key} />
        };

        return icons[key] || <IconGrid title={key} />;
    }
}

export default BudgetService;
